import { useState, useEffect, useRef } from "react";
import {
    User, Moon, Sun, Trash2, AlertTriangle, Save,
    Image as ImageIcon, Download, Upload, Check
} from "lucide-react";
import "./Config.css";

export default function Config() {
    // --- ESTADOS ---
    const [userName, setUserName] = useState(
        () => localStorage.getItem("myWorkspaceName") || ""
    );
    const [userAvatar, setUserAvatar] = useState(
        () => localStorage.getItem("myWorkspaceAvatar") || ""
    );

    const fileInputRef = useRef(null);
    const saveBtnRef = useRef(null);

    const [theme, setTheme] = useState(
        () => localStorage.getItem("viniHubTheme") || "dark"
    );

    const [savedFeedback, setSavedFeedback] = useState(false);

    // --- APLICA TEMA + AVISA OUTROS COMPONENTES ---
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("viniHubTheme", theme);
        window.dispatchEvent(new Event("workspaceThemeChanged"));
    }, [theme]);

    // --- SALVAR PERFIL ---
    const handleSaveProfile = () => {
        localStorage.setItem("myWorkspaceName", userName);
        localStorage.setItem("myWorkspaceAvatar", userAvatar);

        // Avisa a sidebar (ou qualquer componente ouvindo)
        window.dispatchEvent(new Event("workspaceProfileChanged"));

        // Feedback via estado do React (não mexe no DOM)
        setSavedFeedback(true);
        setTimeout(() => setSavedFeedback(false), 2000);
    };

    // --- EXPORTAR DADOS (BACKUP) ---
    const handleExportData = () => {
        const data = {
            myWorkspaceName: localStorage.getItem("myWorkspaceName"),
            myWorkspaceAvatar: localStorage.getItem("myWorkspaceAvatar"),
            viniHubTheme: localStorage.getItem("viniHubTheme"),
            // ⚠️ CONFIRME AS CHAVES ABAIXO OLHANDO CADA ARQUIVO .JSX
            myWorkspaceBooks: localStorage.getItem("myWorkspaceBooks"),
            myWorkspaceSeries: localStorage.getItem("myWorkspaceSeries"),
            myWorkspaceTasksKanban: localStorage.getItem("myWorkspaceTasksKanban"),
            myWorkspaceLinks: localStorage.getItem("myWorkspaceLinks"),
            myWorkspaceAgenda: localStorage.getItem("myWorkspaceAgenda"), // ← corrigido
            myWorkspaceNotes: localStorage.getItem("myWorkspaceNotes"),
            backupDate: new Date().toISOString(),
            backupVersion: 2
        };

        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const href = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = href;
        link.download = `vinihub_backup_${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href); // libera memória
    };

    // --- IMPORTAR DADOS (RESTORE) ---
    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const json = JSON.parse(event.target.result);

                const confirmed = window.confirm(
                    "Isso irá substituir TODOS os seus dados atuais pelos do arquivo. Continuar?"
                );
                if (!confirmed) return;

                // Estratégia: limpa tudo e importa só o que veio no backup
                localStorage.clear();

                Object.entries(json).forEach(([key, value]) => {
                    if (key === "backupDate" || key === "backupVersion") return;
                    if (value === null || value === undefined) return;
                    localStorage.setItem(key, value);
                });

                alert("Backup restaurado com sucesso!");
                window.location.reload();
            } catch (error) {
                alert("Erro ao ler o arquivo de backup. Verifique se é um JSON válido.");
            }
        };
        reader.readAsText(file);
        e.target.value = null;
    };

    // --- RESETAR TUDO ---
    const handleResetApp = () => {
        if (window.confirm("Tem certeza? Isso apagará TODOS os seus dados.")) {
            localStorage.clear();
            // Mantém o usuário na mesma URL, apenas recarrega
            window.location.reload();
        }
    };

    return (
        <div className="config-container">
            <header className="config-header">
                <div>
                    <h1>Configurações</h1>
                    <p>Personalize sua experiência no ViniHub.</p>
                </div>
            </header>

            {/* SEÇÃO 1: APARÊNCIA */}
            <div className="config-section">
                <h2>Aparência</h2>
                <div className="theme-selector">
                    <button
                        className={`theme-btn ${theme === "light" ? "active" : ""}`}
                        onClick={() => setTheme("light")}
                    >
                        <Sun size={24} />
                        <span>Claro</span>
                        {theme === "light" && (
                            <div className="check-badge"><Check size={12} /></div>
                        )}
                    </button>

                    <button
                        className={`theme-btn ${theme === "dark" ? "active" : ""}`}
                        onClick={() => setTheme("dark")}
                    >
                        <Moon size={24} />
                        <span>Escuro</span>
                        {theme === "dark" && (
                            <div className="check-badge"><Check size={12} /></div>
                        )}
                    </button>
                </div>
            </div>

            {/* SEÇÃO 2: PERFIL */}
            <div className="config-section">
                <h2><User size={20} /> Perfil</h2>

                <div className="input-group">
                    <label>Seu Nome</label>
                    <input
                        type="text"
                        placeholder="Como você quer ser chamado?"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                    />
                </div>

                <div className="input-group">
                    <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <ImageIcon size={16} /> URL da Foto (Avatar)
                    </label>
                    <input
                        type="text"
                        placeholder="Cole o link de uma imagem aqui..."
                        value={userAvatar}
                        onChange={(e) => setUserAvatar(e.target.value)}
                    />
                </div>

                {userAvatar && (
                    <div
                        style={{
                            marginTop: "15px",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}
                    >
                        <span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>
                            Pré-visualização:
                        </span>
                        <img
                            src={userAvatar}
                            alt="Preview"
                            style={{
                                width: "50px",
                                height: "50px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid var(--primary)"
                            }}
                            onError={(e) => (e.target.style.display = "none")}
                        />
                    </div>
                )}

                <button ref={saveBtnRef} className="btn-save" onClick={handleSaveProfile}>
                    <Save size={18} /> {savedFeedback ? "Salvo! ✓" : "Salvar Perfil"}
                </button>
            </div>

            {/* SEÇÃO 3: GESTÃO DE DADOS (BACKUP) */}
            <div className="config-section">
                <h2><Save size={20} /> Gestão de Dados</h2>
                <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "15px" }}>
                    Salve seus dados em um arquivo seguro ou restaure um backup antigo.
                </p>

                <div className="data-actions">
                    <button className="btn-export" onClick={handleExportData}>
                        <Download size={18} /> Exportar Backup (JSON)
                    </button>

                    <button className="btn-import" onClick={handleImportClick}>
                        <Upload size={18} /> Restaurar Backup
                    </button>

                    <input
                        type="file"
                        accept=".json"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                </div>
            </div>

            {/* SEÇÃO 4: ZONA DE PERIGO */}
            <div className="config-section danger-zone">
                <h2><AlertTriangle size={20} /> Zona de Perigo</h2>
                <p className="danger-text">
                    Deseja recomeçar do zero? Esta ação apagará todas as tarefas, séries e livros.
                </p>
                <button className="btn-reset" onClick={handleResetApp}>
                    <Trash2 size={18} /> Apagar todos os dados
                </button>
            </div>
        </div>
    );
}